import { Icon } from '@iconify/react';
import { MouseEvent, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { AlertDto } from '@/Domain/api/Conversation/dto/Alert.dto';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { AlertFormValues } from '@/Domain/hooks/AI/useAlertForm/useAlertForm.hook';

type Props = {
  form: UseFormReturn<AlertFormValues>;
  index: number;
  alert: AlertDto;
  isEditMode: boolean;
  isUpdatePending: boolean;
  setIsEditMode: (mode: boolean) => void;
  onDelete: (alertId: number) => void;
  onReset: () => void;
  onToggleActive: (next: boolean) => void;
};

export const AlertFormItem = ({
  index,
  alert,
  form,
  onDelete,
  onReset,
  onToggleActive,
  isUpdatePending,
  isEditMode,
  setIsEditMode,
}: Props) => {
  const { t } = useTranslation('insights_page');

  const handleEditState = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditMode(true);
  };

  const handleDelete = () => onDelete(alert.id);

  const handleReset = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onReset();
    setIsEditMode(false);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  return (
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <section className="grid grid-cols-[0.5fr_2fr_4fr_auto_auto] gap-x-2 items-start py-2">
        <p className="mt-2 text-foreground-subtle text-sm whitespace-nowrap">
          {t('manage_alerts.form.alert', { index: index + 1 })}
        </p>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                {isEditMode ? (
                  <Input
                    autoFocus
                    placeholder={t('manage_alerts.form.name_placeholder')}
                    size="sm"
                    {...field}
                  />
                ) : (
                  <p className="text-sm px-4 py-2 border border-transparent">{alert.name}</p>
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                {isEditMode ? (
                  <AutosizeTextarea
                    maxLength={500}
                    autoComplete="off"
                    className="rounded-xl !min-h-[34px] !px-4 pb-2 pt-[9px] leading-none break-words"
                    placeholder={t('manage_alerts.form.description_placeholder')}
                    minHeight={12}
                    {...field}
                  />
                ) : (
                  <p
                    className="text-sm px-4 py-2 border border-transparent whitespace-pre-line"
                    style={{ wordBreak: 'break-word' }}
                  >
                    {alert.description}
                  </p>
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center h-[34px] px-2">
              <Switch
                checked={form.watch('is_active')}
                onCheckedChange={onToggleActive}
                disabled={!alert.name || isEditMode}
                data-testid={`alert-toggle-${alert.id}`}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {form.watch('is_active') ? t('tooltip.deactivate_alert') : t('tooltip.activate_alert')}
          </TooltipContent>
        </Tooltip>

        {isEditMode ? (
          <section className="flex gap-2">
            <Button
              variant="secondary"
              size="icon"
              type="submit"
              disabled={isUpdatePending}
              data-testid={`alert-save-${alert.id}`}
            >
              <Icon icon="mingcute:check-line" />
            </Button>
            <Button variant="danger" size="icon" disabled={!alert.name} onClick={handleReset}>
              <Icon icon="mingcute:close-line" />
            </Button>
          </section>
        ) : (
          <section className="flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={handleEditState}
                  data-testid={`alert-edit-${alert.id}`}
                >
                  <Icon icon="mingcute:pencil-line" />
                </Button>
              </TooltipTrigger>
              <TooltipContent align="end" alignOffset={-135}>
                <Trans i18nKey="insights_page:tooltip.edit_alert" />
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild type="button">
                <AlertDialogTrigger asChild type="button">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="hover:!text-red-500"
                    data-testid={`alert-delete-trigger-${alert.id}`}
                  >
                    <Icon icon="mingcute:delete-line" />
                  </Button>
                </AlertDialogTrigger>
              </TooltipTrigger>
              <TooltipContent align="end">{t('tooltip.delete_alert')}</TooltipContent>
            </Tooltip>
          </section>
        )}
      </section>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Icon
              icon="mingcute:alert-fill"
              className="bg-red-100 rounded-lg text-red-500 p-2 box-content"
            />
            {t('dialog.delete_alert.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('dialog.delete_alert.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <Separator />
        <AlertDialogFooter>
          <AlertDialogCancel>
            <Button size="sm" variant="secondary">
              {t('dialog.delete_alert.button.cancel')}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete}>
            {t('dialog.delete_alert.button.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
